import { lazy, Suspense, useEffect } from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import { Skeleton } from '@vta/ui';
import type { RootSearch } from '../lib/url-state.ts';
import { useSelectedTeamStore } from '../stores/selected-team-store.ts';

/**
 * Homepage — full-bleed interactive globe.
 *
 * The globe scene is a heavy bundle (three.js + react-globe.gl + globe
 * texture). We lazy-import it so the initial paint is the layout shell only;
 * the globe loads on idle. While suspended we render a shimmer placeholder
 * sized to the viewport.
 */
const GlobeScene = lazy(() =>
  import('../features/globe/GlobeScene.tsx').then((m) => ({ default: m.GlobeScene })),
);

export const Route = createFileRoute('/')({
  component: HomeComponent,
});

function HomeComponent() {
  // We pull search from useRouterState rather than `Route.useSearch()` so the
  // typing is independent of the generated route tree (which is regenerated
  // by the Vite plugin and uses `@ts-nocheck` while we wait for sibling
  // routes to land).
  const search = useRouterState({ select: (s) => s.location.search as RootSearch });
  const openTeam = useSelectedTeamStore((s) => s.openTeam);
  const openCity = useSelectedTeamStore((s) => s.openCity);
  const isOpen = useSelectedTeamStore((s) => s.isOpen);
  const selection = useSelectedTeamStore((s) => s.selection);
  const navigate = useNavigate();

  // Hydrate drawer from `?team=` / `?city=` deep links (also the path the
  // command palette takes: search picks write these params and the globe
  // flies to the marker). Guarded so a URL value only opens when it differs
  // from the current selection — closing the drawer doesn't re-open it.
  useEffect(() => {
    if (search.team) {
      if (selection.kind !== 'team' || selection.teamNumber !== search.team) {
        openTeam(search.team);
      }
      return;
    }
    if (search.city) {
      if (selection.kind !== 'city' || selection.citySlug !== search.city) {
        // Name/country resolve from `/api/cities/:slug` inside the drawer;
        // the slug doubles as the optimistic heading fallback.
        openCity({ citySlug: search.city, city: humanizeSlug(search.city), country: null });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.team, search.city, openTeam, openCity]);

  // When the user closes the drawer we sync the URL too so deep-link state
  // stays accurate (the drawer's onOpenChange dispatches close()).
  //
  // Reads live store state (not the render snapshot): on a fresh deep-link
  // load the hydrate effect above opens the drawer in the same commit, and
  // the stale `isOpen=false` snapshot here would immediately strip the param.
  useEffect(() => {
    const live = useSelectedTeamStore.getState().isOpen;
    if (!live && (search.team || search.city)) {
      navigate({
        to: '.',
        from: '/',
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          team: undefined,
          city: undefined,
        }),
        replace: true,
      });
    }
  }, [isOpen, search.team, search.city, navigate]);

  return (
    <Suspense fallback={<GlobeFallback />}>
      <GlobeScene />
    </Suspense>
  );
}

function humanizeSlug(slug: string): string {
  return slug
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function GlobeFallback() {
  return (
    <div className="absolute inset-0 overflow-hidden bg-bg-sunken">
      <Skeleton className="absolute left-1/2 top-1/2 h-[min(72vmin,720px)] w-[min(72vmin,720px)] -translate-x-1/2 -translate-y-1/2 rounded-full" />
      <p
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-2xs uppercase tracking-[0.32em] text-fg-subtle"
        aria-live="polite"
      >
        Spinning up the atlas
      </p>
    </div>
  );
}
